import {Container, Graphics} from "pixi.js";
import {Polygon} from "detect-collisions";
import {lerp, round} from "../utils.js";
import {isVectorable, Vector} from "../i.js";
import {Random} from "../i.js";
import {logger} from "../i.js";
import {
	ConfigStructure, EitherOr, InvalidConfigError, OptionalProperty, RequiredProperty
} from "../i.js";
import {Particles} from "../i.js";
import {AI} from "../i.js";
import {FACTIONS, Thing, THING_TYPES, Z_INDICES} from "../i.js";



// Range of axon lifespans, in seconds
const LIFESPAN_MIN = 6;
const LIFESPAN_MAX = 12;
// After carrying signal, an axon can't be removed by lifespan for this long, in seconds
//
// This value also determines the length of the pre-ageout shrink animation.
const AGEOUT_PROTECTION = 0.75;
// Time until a new axon becomes ready, in seconds
const TIME_UNTIL_READY = 1.5;

// Length of a single-parent axon, in flares
const UNCAPPED_AXON_LENGTH = 16;
// Speeds of stuff moving along axons, in flares / second
const SIGNAL_SPEED = 2;
const BURN_SPEED = 2;

// Visuals

// Color of the outline of the axon
const COLOR_OUTLINE = {h: 10, s: 100, v: 100};
// Color of the center of the axon
const COLOR_CENTER = {h: 10, s: 5, v: 100};
// Number of zigzags in each axon
const NUM_ZIGZAGS = 3;
// Average segment length in each zigzag, in flares
// Length of this array must match `NUM_ZIGZAGS`.
const AVERAGE_SEGMENT_LENGTHS = [0.18, 0.23, 0.28];
// How much the zigzag segment length can vary from segment to segment
const SEGMENT_LENGTH_VARIETY = 0.5;
// How far the zigzags can steer from the center line, in flares
const AMPLITUDE = 0.045;
// How fast the zigzags change shape
const JITTER_SPEED = 2.5;
// How jagged the zigzags are
const ZIGZAG_NOISE_DENSITY = 4.5;
// The amplitude increase when the axon is disabled by Cossette, relative to standard
// amplitude
const COSSETTE_AMPLITUDE = 2;
// How wide a signal is along the length the axon, in flares
const SIGNAL_WIDTH = 0.3;
// The amplitude increase in the middle of a signal, relative to standard amplitude
const SIGNAL_AMPLITUDE = 6;
// Offsets for extra points added around each signal, in fractions of `SIGNAL_WIDTH`
//
// -0.5 is the start of a signal, 0.5 is the end of a signal.
//
// Each offset is an array with two numbers representing a range. The first zigzag uses
// the starts of ranges, the last zigzag uses the ends of ranges.
//
// The number of offsets here determines the number of points added around each signal.
const SIGNAL_POINT_OFFSETS = [[-0.6, -0.4], [-0.3, -0.05], [0.05, 0.3], [0.4, 0.6]];
// How many particles to emit when an axon disappears naturally, in particles per flare
const SHATTER_PARTICLE_DENSITY = 7;
// Time between spark particles on each burning end
const TIME_BETWEEN_PARTICLES = 0.02;



export function selectAxonLifespan() {
	return round(Random.uniform(LIFESPAN_MIN, LIFESPAN_MAX), 2);
}



class AxonAI extends AI {
	// Default constructor
	// constructor(thing) {
	// 	super(thing);
	// }

	move(dt) {
		let firstParent = this.thing.space.requestThingById(this.thing.firstParentId);
		let secondParent = this.thing.isLaser
			? null
			: this.thing.space.requestThingById(this.thing.secondParentId);

		this._updateAnchors(firstParent, secondParent);
		if (!this._updateBurn(dt, firstParent, secondParent)) return;
		this._updateSignals(dt, firstParent, secondParent);
		this._checkLifespan(dt, firstParent, secondParent);
		this._checkForCossette();
		this._updateHitbox();
	}

	_updateAnchors(firstParent, secondParent) {
		if (firstParent !== null) this.thing.calculateFirstAnchor(firstParent);
		if (this.thing.isLaser || secondParent !== null) {
			this.thing.calculateSecondAnchor(secondParent);
		}

		let firstAnchor = this.thing.firstAnchor;
		let secondAnchor = this.thing.secondAnchor;

		this.thing.len = Vector.diff(secondAnchor, firstAnchor).mag();
		// TODO: Get rid of radius
		this.thing.radius = this.thing.len / 2;
		this.thing.pos = Vector.sum(firstAnchor, secondAnchor).scale(0.5);
		// vel remains (0, 0), for now - don't know what the best way to handle it is
		// `- Math.PI / 2` undoes `+ Math.PI / 2` in Thing
		this.thing.rot = Vector.diff(secondAnchor, firstAnchor).dir() - Math.PI / 2;
	}

	// Returns true iff axon is still alive
	_updateBurn(dt, firstParent, secondParent) {
		if (firstParent === null) {
			if (this.thing.isLaser) {
				// I tried allowing lasers to burn, but then a burning laser would exist
				// for so much longer than a non-burning one and it created a weird
				// obstacle in the Damsel fight
				this.thing.prepareToDie(false);
				return false;
			} else {
				this.thing.burn.start.burning = true;
			}
		}
		if (!this.thing.isLaser && secondParent === null) {
			this.thing.burn.end.burning = true;
		}

		if (this.thing.burn.start.burning) {
			this.thing.burn.start.location += BURN_SPEED * dt / this.thing.len;
			if (this.thing.burn.start.location >= 1 && secondParent !== null) {
				secondParent.receiveBurn(this.thing);
			}
		}
		if (this.thing.burn.end.burning) {
			this.thing.burn.end.location -= BURN_SPEED * dt / this.thing.len;
			if (this.thing.burn.end.location <= 0 && firstParent !== null) {
				firstParent.receiveBurn(this.thing);
			}
		}
		if (this.thing.burn.start.location >= this.thing.burn.end.location) {
			this.thing.prepareToDie(false);
			return false;
		}
		return true;
	}

	_updateSignals(dt, firstParent, secondParent) {
		for (let i = this.thing.signals.length; i--;) {
			let signal = this.thing.signals[i];
			signal.location += ((signal.direction === "forward") ? 1 : -1)
				* (SIGNAL_SPEED * dt) / this.thing.len;
			if (signal.direction === "forward"
							&& signal.location >= this.thing.burn.end.location) {
				// If the signal sinks in burn, then the parent doesn't exist
				// So this check is enough!
				if (secondParent !== null) secondParent.receiveSignal();
				this.thing.signals.splice(i, 1);
			}
			if (signal.direction === "backward"
							&& signal.location <= this.thing.burn.start.location) {
				// If the signal sinks in burn, then the parent doesn't exist
				// So this check is enough!
				if (firstParent !== null) firstParent.receiveSignal();
				this.thing.signals.splice(i, 1);
			}
		}
	}

	// Returns true iff axon is still alive
	_checkLifespan(dt, firstParent, secondParent) {
		this.thing.age += dt;

		// If the axon was ready when it started burning, it will remain ready
		// But an axon won't become ready mid-burn
		if (!this.thing.burn.start.burning && !this.thing.burn.end.burning
						&& this.thing.age >= TIME_UNTIL_READY) {
			this.thing.ready = true;
		}

		if (this.thing.lifespan === 0) return true;

		// Check if the axon needs to be removed

		// Prevent axons from suddenly disappearing while carrying burn / signal,
		// or right after
		if (this.thing.burn.start.burning || this.thing.burn.end.burning
						|| this.thing.signals.length) {
			// I considered a separate timer for this, but bumping up the lifespan
			// accomplishes the same thing easier
			this.thing.lifespan = Math.max(
				this.thing.lifespan,
				this.thing.age + AGEOUT_PROTECTION
			);
		}
		if (this.thing.age > this.thing.lifespan) {
			if (firstParent !== null) firstParent.removeOutgoingAxon();
			if (secondParent !== null) secondParent.removeIncomingAxon(this.thing.id);
			this.thing.prepareToDie(false);
			this._addShatterParticles();
			return false;
		}

		return true;
	}

	_checkForCossette() {
		this.thing.disabledByCossette = false;

		let tacticalReport = this.thing.space.requestTacticalReport(
			entry => entry.identity === "COSSETTE",
			"AXON_COSSETTE"
		);
		if (!tacticalReport.length) return;
		let cossettePos = tacticalReport[0].pos;

		// Fast path: return if Cossette is > 20 flares away
		if (Vector.dist2(this.thing.pos, cossettePos) > 400) return;

		let firstAnchor = this.thing.firstAnchor;
		let secondAnchor = this.thing.secondAnchor;
		if (cossettePos.distToLineSegment(firstAnchor, secondAnchor) < 4) {
			this.thing.ready = false;
			this.thing.disabledByCossette = true;
		}
	}

	_updateHitbox() {
		// Weird hitbox to account for `- Math.PI / 2` in rot
		// TODO: Once that rot adjustment is no longer needed, swap x and y
		this.thing.hitbox.setPoints([
			{
				x: 0,
				y: lerp(
					- this.thing.len / 2,
					this.thing.len / 2,
					this.thing.burn.start.location
				)
			},
			{
				x: 0,
				y: lerp(
					- this.thing.len / 2,
					this.thing.len / 2,
					this.thing.burn.end.location
				)
			}
		]);
	}

	// Add particles when the axon disappears naturally
	_addShatterParticles() {
		let firstAnchor = this.thing.firstAnchor;
		let secondAnchor = this.thing.secondAnchor;

		let numParticles = Math.round(SHATTER_PARTICLE_DENSITY * this.thing.len);

		for (let i = 0; i < numParticles; ++i) {
			this.thing.space.particles.add(
				Particles.TYPES.AXON,
				Vector.interpolate(
					this.thing.firstAnchor,
					this.thing.secondAnchor,
					(i + 0.5) / numParticles
				),
				Vector.diff(secondAnchor, firstAnchor),
				1
			);
		}
	}
}



// Slice (y) of the noise to use
// Incremented each time it's queried
let noiseSlice = 0;

class AxonAnimation {
	constructor(thing) {
		this.thing = thing;

		this._zigzags = AVERAGE_SEGMENT_LENGTHS.map(segmentLength => ({
			// Each point is a number from 0 to 1 inclusive
			points: [],
			noiseStart: 0,
			noiseWidth: 1,
			averageSegmentLength: segmentLength
		}));

		// How many particles are queued for drawing on each burning end
		//
		// Between frames, this will be a fraction from 0 to 1. We store this info to emit
		// particles at a consistent rate regardless of the framerate.
		this._particlesToDraw = {
			start: 0,
			end: 0
		};

		this._outline = new Container();
		this._center = new Container();
		for (let i = 0; i < NUM_ZIGZAGS; ++i) {
			this._outline.addChild(new Graphics());
			this._center.addChild(new Graphics());
		}

		this.thing.display.addChild(this._outline, this._center);
	}

	update(dt, time) {
		for (let i = 0; i < NUM_ZIGZAGS; ++i) {
			this._outline.children[i].clear();
			this._center.children[i].clear();
		}

		let fractionReady = Math.min(this.thing.age / TIME_UNTIL_READY, 1);
		this._calculatePoints();
		let burnStart = this.thing.burn.start.location;
		let burnEnd = this.thing.burn.end.location;

		let unusualActivity = (this.thing.signals.length > 0)
			|| this.thing.burn.start.burning || this.thing.burn.end.burning;

		let currentNumZigzags = NUM_ZIGZAGS;
		// Alternative: let currentNumZigzags = Math.ceil(NUM_ZIGZAGS * fractionReady);
		for (let i = 0; i < currentNumZigzags; ++i) {
			let zigzag = this._zigzags[i];

			let points = zigzag.points;
			if (unusualActivity) {
				// At any given time, most axons have nothing going on, so this block
				// doesn't get executed that many times.
				points = Array.from(zigzag.points);
				this.thing.signals.forEach(signal => {
					// Add extra points around the signal location on each zigzag to make
					// the signal more visible.
					for (let [offsetRangeStart, offsetRangeEnd] of SIGNAL_POINT_OFFSETS) {
						// Divide offsets by `this.thing.len` for unit conversion: we want
						// to offset the points by a fixed number of flares, but point
						// locations in the `points` array are fractions of the thing len.
						let offset = lerp(
							offsetRangeStart * SIGNAL_WIDTH,
							offsetRangeEnd * SIGNAL_WIDTH,
							i / (NUM_ZIGZAGS - 1)
						) / this.thing.len;
						points.push(signal.location + offset);
					}
				});
				// I considered adding/subtracting a small epsilon from burn ends to make
				// sure the comparisons below go the correct way. But we don't do any math
				// with these numbers, so I believe comparing them is safe without extra
				// epsilons.
				if (this.thing.burn.start.burning) points.push(burnStart);
				if (this.thing.burn.end.burning) points.push(burnEnd);
				points.sort();
			}

			let pointsToDraw = [];
			points.forEach(p => {
				if (p < burnStart || p > burnEnd) return;
				let x = lerp(- this.thing.len / 2, this.thing.len / 2, p);
				let y = AMPLITUDE * Random.noise(
					JITTER_SPEED * time,
					zigzag.noiseStart + p * zigzag.noiseWidth
				);

				// Calculate distances to anything interesting
				let minDistToSignal = Infinity;
				this.thing.signals.forEach(signal => {
					minDistToSignal = Math.min(
						minDistToSignal,
						Math.abs(p - signal.location) * this.thing.len
					);
				});

				// Increase amplitude as needed
				let cossetteAmplitudeIncrease = this.thing.disabledByCossette
					? COSSETTE_AMPLITUDE : 1;
				let signalAmplitudeIncrease = Math.max(
					SIGNAL_AMPLITUDE * (1 - (2 * minDistToSignal / SIGNAL_WIDTH) ** 2),
					1
				);
				y *= Math.max(cossetteAmplitudeIncrease, signalAmplitudeIncrease);

				// Reduce amplitude right before ageout
				if (this.thing.lifespan > 0) {
					y *= Math.min(
						(this.thing.lifespan - this.thing.age) / AGEOUT_PROTECTION,
						1
					);
				}

				pointsToDraw.push(new Vector(x, y));
			});

			this._outline.children[i]
				.poly(pointsToDraw, false)
				.stroke({
					width: this.thing.ready ? 0.045 : 0.025,
					color: COLOR_OUTLINE,
					cap: "round"
				});
			this._outline.alpha = this.thing.ready ? 1 : (0.8 * fractionReady);
			if (this.thing.ready)
				this._center.children[i]
					.poly(pointsToDraw, false)
					.stroke({
						width: 0.02,
						color: COLOR_CENTER,
						cap: "round"
					});
		}

		this._addParticles(dt);
	}

	_calculatePoints() {
		for (let zigzag of this._zigzags) {
			let numSegments = Math.round(this.thing.len / zigzag.averageSegmentLength);
			if (zigzag.points.length === numSegments + 1) return;

			// Recalculate from scratch

			let segmentLengths = [];
			let totalLength = 0;
			for (let i = 0; i < numSegments; ++i) {
				let segmentLength = Random.uniform(1, 1 + SEGMENT_LENGTH_VARIETY);
				segmentLengths.push(segmentLength);
				totalLength += segmentLength;
			}

			zigzag.points = [];
			let runningLength = 0;
			for (let i = 0; i < numSegments + 1; ++i) {
				if (i > 0) runningLength += segmentLengths[i - 1];
				zigzag.points.push(runningLength / totalLength);
			}

			zigzag.noiseStart = noiseSlice;
			zigzag.noiseWidth = ZIGZAG_NOISE_DENSITY * this.thing.len;
			noiseSlice = (noiseSlice + 64) % 100000;
		}
	}

	// Add particles at burning ends
	_addParticles(dt) {
		if (this.thing.burn.start.burning) {
			this._particlesToDraw.start += dt / TIME_BETWEEN_PARTICLES;
			this.thing.space.particles.add(
				Particles.TYPES.SPARK,
				Vector.interpolate(
					this.thing.firstAnchor,
					this.thing.secondAnchor,
					this.thing.burn.start.location
				),
				Vector.diff(this.thing.secondAnchor, this.thing.firstAnchor),
				Math.floor(this._particlesToDraw.start)
			);
			this._particlesToDraw.start = this._particlesToDraw.start % 1;
		}
		if (this.thing.burn.end.burning) {
			this._particlesToDraw.end += dt / TIME_BETWEEN_PARTICLES;
			this.thing.space.particles.add(
				Particles.TYPES.SPARK,
				Vector.interpolate(
					this.thing.firstAnchor,
					this.thing.secondAnchor,
					this.thing.burn.end.location
				),
				Vector.diff(this.thing.firstAnchor, this.thing.secondAnchor),
				Math.floor(this._particlesToDraw.end)
			);
			this._particlesToDraw.end = this._particlesToDraw.end % 1;
		}
	}
}



export class Axon extends Thing {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// id of the axon's first parent
		// Must be accessible at creation time, unless `firstAnchor` is also provided.
		new RequiredProperty("firstParentId", v => Number.isFinite(v)),
		// Offset of the start of the axon relative to center of first parent
		// Defaults to (0, 0) if omitted.
		new OptionalProperty("offsetFirst", v => isVectorable(v)),
		// pos at which the axon starts, vector in flares
		new OptionalProperty("firstAnchor", v => isVectorable(v)),
		new EitherOr([
			// id of the axon's second parent
			// Must be accessible at creation time, unless `secondAnchor` is also
			// provided.
			new RequiredProperty("secondParentId", v => Number.isFinite(v)),
			// Offset of the end of the axon relative to center of second parent
			// Defaults to (0, 0) if omitted.
			new OptionalProperty("offsetSecond", v => isVectorable(v))
		], [
			// Absolute angle at which the axon originates from the first (and in this
			// case, only) parent
			// Not relative to parent's rot. Takes precedence over second parent.
			new RequiredProperty("angle", v => Number.isFinite(v))
		]),
		// pos at which the axon ends, vector in flares
		new OptionalProperty("secondAnchor", v => isVectorable(v)),
		// Age of this axon in seconds, defaults to 0 if omitted
		new OptionalProperty("age", v => Number.isFinite(v) && v >= 0),
		// Lifespan of this axon, 0 for unlimited lifespan, randomly chosen if omitted
		new OptionalProperty("lifespan", v => Number.isFinite(v) && v >= 0),
		// Whether the axon belongs to a boss
		// Must be true if specified, defaults to false if omitted.
		new OptionalProperty("belongsToBoss", v => (v === true)),
		// Whether the axon is ready
		// Must be true if specified, defaults to false if omitted.
		new OptionalProperty("ready", v => (v === true)),
		// Array of signals
		// Each signal is a string, where the first character is "f" for forward or "b"
		// for backward, and the rest of the string is the location of the signal.
		// Example: ["f0.3", "b0.55"]
		// Defaults to no signals if omitted.
		new OptionalProperty("signals", v => {
			if (!Array.isArray(v)) return false;
			for (let signal of v) {
				if (typeof signal !== "string") return false;
				if (signal[0] !== "f" && signal[0] !== "b") return false;
				let location = parseFloat(signal.substring(1));
				if (!(Number.isFinite(location) && location >= 0 && location <= 1)) {
					return false;
				}
			}
			return true;
		}),
		// If specified: start is burning and this value is the location of the burn along
		// the axon, 0 to 1 inclusive.
		// If omitted: start is not burning.
		new OptionalProperty("burnStart", v => Number.isFinite(v) && v >= 0 && v <= 1),
		// If specified: end is burning and this value is the location of the burn along
		// the axon, 0 to 1 inclusive.
		// If omitted: end is not burning.
		new OptionalProperty("burnEnd", v => Number.isFinite(v) && v >= 0 && v <= 1)
	]);

	// Create a new axon
	// config is an object with the properties provided by the spawn request
	// enrichment, plus the properties specified in CONFIG_STRUCTURE above.
	constructor(config) {
		Axon.CONFIG_STRUCTURE.test(config);

		// Fill in missing config fields
		config.identity = "AXON";
		config.name = "Axon";
		config.zIndex = (config.belongsToBoss === true)
			? Z_INDICES.BOSS_CHILD
			: Z_INDICES.NPC_CHILD;
		config.len = 1;
		config.hitbox = new Polygon({x: 0, y: 0}, [
			{x: -1, y: 0},
			{x:  1, y: 0}
		]);
		config.pos = new Vector();
		config.vel = new Vector();
		config.rot = 0;
		config.maxHealth = 9;
		config.type = THING_TYPES.ENERGY;
		config.faction = FACTIONS.DAMSEL;
		config.canDespawn = false;

		// Call parent constructor
		super(config);

		// Handle parent / offset / anchor info

		this.isLaser = config.angle !== undefined;
		let firstParent = this.space.requestThingById(config.firstParentId);
		let secondParent = this.isLaser
			? null
			: this.space.requestThingById(config.secondParentId);

		this.firstParentId = config.firstParentId;
		// Works even if the offsets are undefined!
		this.offsetFirst = new Vector(config.offsetFirst);

		if (config.firstAnchor !== undefined) {
			this.firstAnchor = new Vector(config.firstAnchor);
		} else {
			if (firstParent === null) throw new InvalidConfigError(
				"firstAnchor was not specified and firstParent couldn't be found "
				+ `(firstParentId === ${config.firstParentId})`
			);
			this.calculateFirstAnchor(firstParent);
		}

		if (this.isLaser) {
			this.angle = config.angle;
		} else {
			this.secondParentId = config.secondParentId;
			// Works even if the offsets are undefined!
			this.offsetSecond = new Vector(config.offsetSecond);
		}

		if (config.secondAnchor !== undefined) {
			this.secondAnchor = new Vector(config.secondAnchor);
		} else {
			if (!this.isLaser && secondParent === null) throw new InvalidConfigError(
				"secondAnchor was not specified, the axon is not a laser, and "
				+ "secondParent couldn't be found "
				+ `(secondParentId === ${config.secondParentId})`
			);
			this.calculateSecondAnchor(secondParent);
		}

		if (firstParent !== null) {
			firstParent.addOutgoingAxon(this.id);
		}
		if (secondParent !== null) {
			secondParent.addIncomingAxon(this.id, this.firstParentId);
		}

		// Handle various one-off values

		this.age = config.age ?? 0;
		this.lifespan = (config.lifespan !== undefined)
			? config.lifespan
			: selectAxonLifespan();
		// Keep `belongsToBoss` for saving
		this._belongsToBoss = config.belongsToBoss;
		// TODO: Autofill ready based on age, allowing overwrite?
		this.ready = (config.ready === true);

		// Handle signal and burn

		// Each signal is an object with properties:
		//   location  - number 0 to 1 inclusive; 0 is start, 1 is end
		//   direction - "forward" or "backward"
		this.signals = (config.signals ?? []).map(signal => ({
			location: parseFloat(signal.substring(1)),
			direction: (signal[0] === "f") ? "forward" : "backward"
		}));

		// location is a number 0 to 1 inclusive; 0 is start, 1 is end
		this.burn = {
			start: {
				burning: config.burnStart !== undefined,
				location: config.burnStart ?? 0
			},
			end: {
				burning: config.burnEnd !== undefined,
				location: config.burnEnd ?? 1
			}
		};

		// Whether the axon is disabled by Cossette
		// Set by AI and read by the animation; no need to save this value.
		this.disabledByCossette = false;

		// Create AI and animation

		this.ai = new AxonAI(this);
		this.animation = new AxonAnimation(this);
	}

	// Args:
	//   direction - "forward" or "backward"
	receiveSignal(direction) {
		this.signals.push({
			location: (direction === "forward") ? 0 : 1,
			direction: direction
		});
	}

	save() {
		let config = super.save();

		config.firstParentId = this.firstParentId;
		config.firstAnchor = this.firstAnchor.save();
		if (this.offsetFirst.x !== 0 || this.offsetFirst.y !== 0) {
			config.offsetFirst = this.offsetFirst.save();
		}
		if (this.isLaser) {
			config.angle = round(this.angle);
		} else {
			config.secondParentId = this.secondParentId;
			if (this.offsetSecond.x !== 0 || this.offsetSecond.y !== 0) {
				config.offsetSecond = this.offsetSecond.save();
			}
		}
		config.secondAnchor = this.secondAnchor.save();
		if (this.age > 0) config.age = round(this.age);
		config.lifespan = round(this.lifespan);
		if (this._belongsToBoss) config.belongsToBoss = true;
		if (this.ready) config.ready = true;
		if (this.signals.length > 0) config.signals = this.signals.map(signal =>
			`${signal.direction === "forward" ? "f" : "b"}${round(signal.location)}`
		);
		if (this.burn.start.burning) config.burnStart = round(this.burn.start.location);
		if (this.burn.end.burning) config.burnEnd = round(this.burn.end.location);

		return config;
	}

	// Take damage from an attack
	// damage is a number.
	takeDamage(damage) {
		// Nothing to do here - a player can't damage an axon directly.
		// TODO: Remove on collision with a station.
	}

	calculateFirstAnchor(firstParent) {
		this.firstAnchor = Vector.sum(
			firstParent.pos,
			new Vector(this.offsetFirst).rotateBy(firstParent.rot)
		);
	}

	// secondParent is only used for non-lasers
	calculateSecondAnchor(secondParent) {
		if (this.isLaser) {
			this.secondAnchor = Vector.sum(
				this.firstAnchor,
				new Vector(UNCAPPED_AXON_LENGTH, 0).rotateBy(this.angle)
			);
		} else {
			this.secondAnchor = Vector.sum(
				secondParent.pos,
				new Vector(this.offsetSecond).rotateBy(secondParent.rot)
			);
		}
	}
}
